import { Field, Formik } from 'formik'
import type { ChangeEvent } from 'react'
import React from 'react'

import { useDashboardPermissions } from '@components/utils/DashboardPermissions'
import { LeavingGuard } from '@components/utils/LeavingGuard'
import {
  ButtonClean,
  Fieldset,
  HeadContent,
  Skeleton,
  Subsection,
  Switch,
  Toasts,
} from '@fourthwall/components'
import { useCurrentShop } from '@modules/Dashboard/hooks/useCurrentShop'
import { ConfirmBar } from '@modules/Dashboard/modules/ConfirmBar'

import { ThankYouCardsUploader } from './ThankYouCardUploader/ThankYouCardUploader'
import * as S from './ThankYouCards.styled'
import { useThankYouCardSettingsMutation } from './hooks/useThankYouCardSettingsMutation'
import { useThankYouCardSettingsQuery } from './hooks/useThankYouCardSettingsQuery'
import { useThankYouCardUploadMutation } from './hooks/useThankYouCardUploadMutation'

export type FormValues = {
  enabled: boolean
  photoUrl: string | undefined
  uploadUrl: string | null
  file: File | null
  option: 'ALL' | 'SELECTED'
  offers: Array<{ id: string; optionTitle: string }>
}

export const ThankYouCards = () => {
  const { currentShop } = useCurrentShop()
  const { dashboardPermissions } = useDashboardPermissions()

  const { thankYouCardUploadMutation } = useThankYouCardUploadMutation()
  const { thankYouCardSettingsMutation } = useThankYouCardSettingsMutation()
  const { thankYouCardSettingsQuery } = useThankYouCardSettingsQuery(
    [{ shopId: currentShop?.id! }],
    {
      enabled: Boolean(currentShop?.id),
    },
  )

  if (!thankYouCardSettingsQuery.data) {
    return <ThankYouCardsSkeleton />
  }

  return (
    <>
      <HeadContent title="Custom inserts &ndash; Settings" />
      <Formik<FormValues>
        initialValues={{
          enabled: thankYouCardSettingsQuery.data.enabled,
          photoUrl: thankYouCardSettingsQuery.data.photoUrl,
          uploadUrl: null,
          file: null,
          option: 'ALL',
          offers: [],
        }}
        enableReinitialize
        onSubmit={async (values: FormValues) => {
          const { enabled, photoUrl, uploadUrl, file } = values

          if (dashboardPermissions.settings.thankYouCards.edit) {
            await thankYouCardSettingsMutation.mutateAsync([
              { shopId: currentShop?.id! },
              {
                enabled,
                photoUrl,
              },
            ])

            if (uploadUrl && file) {
              await fetch(uploadUrl, {
                method: 'PUT',
                headers: {
                  'Content-Disposition': `attachment; filename="${file.name}"`,
                  'Content-Type': file.type,
                  'x-goog-content-length-range': `0,${file.size}`,
                },
                body: file,
              })
            }

            await thankYouCardSettingsQuery.refetch()
          } else {
            Toasts.notify('You do not have permission to edit custom insterts')
          }
        }}
      >
        {({
          values,
          dirty,
          isSubmitting,
          isValid,
          submitForm,
          resetForm,
          handleSubmit,
          setFieldValue,
        }) => (
          <form onSubmit={handleSubmit}>
            <Subsection title="Custom inserts">
              <S.Container>
                <p>
                  Add a custom designed insert to customer’s packages. Made from premium matte card
                  stock.{' '}
                  <ButtonClean
                    label="Learn more"
                    href="https://help.fourthwall.com/hc/en-us/articles/42884664593819"
                    target="_blank"
                  />
                </p>

                <Fieldset>
                  <Fieldset.Row>
                    <Fieldset.Item>
                      <Field
                        component={Switch}
                        name="enabled"
                        label="Add custom insert to orders of supported products ($0.25 per insert)"
                        size="small"
                        onChange={(event: ChangeEvent<HTMLInputElement>) => {
                          setFieldValue('enabled', event.target.checked)
                        }}
                      />
                    </Fieldset.Item>
                  </Fieldset.Row>
                </Fieldset>

                <ThankYouCardsUploader
                  initialImage={values.photoUrl}
                  onConfirm={async (blob) => {
                    const file = new File([blob], 'thank-you-card', { type: blob.type })

                    const data = await thankYouCardUploadMutation.mutateAsync([
                      { shopId: currentShop?.id! },
                      {
                        fileName: file.name,
                        contentType: file.type,
                        size: file.size,
                      },
                    ])

                    setFieldValue('file', file)
                    setFieldValue('uploadUrl', data.uploadUrl)
                    setFieldValue('photoUrl', data.fileUrl)
                  }}
                  onRemove={() => {
                    setFieldValue('file', null)
                    setFieldValue('uploadUrl', null)
                    setFieldValue('photoUrl', null)
                  }}
                />
              </S.Container>
            </Subsection>

            <ConfirmBar
              isOpen={dirty}
              isLoading={isSubmitting}
              onConfirm={submitForm}
              onCancel={resetForm}
            />
            <LeavingGuard when={dirty} isValid={isValid} onSave={submitForm} />
          </form>
        )}
      </Formik>
    </>
  )
}

const ThankYouCardsSkeleton = () => {
  return (
    <>
      <HeadContent title="Custom inserts &ndash; Settings" />
      <Subsection title="Custom inserts">
        <S.Container>
          <p>
            Add a custom designed insert to customer’s packages. Made from premium matte card stock.
          </p>

          <Skeleton>
            <Skeleton.Shape width="100%" height={32} />
          </Skeleton>

          <Skeleton style={{ marginTop: 16 }}>
            <Skeleton.Shape width="100%" height={380} />
          </Skeleton>
        </S.Container>
      </Subsection>
    </>
  )
}
